/**
 * Utility functions for parsing Romanian CNP (Cod Numeric Personal)
 */

export interface CNPData {
  gender: 'masculin' | 'feminin';
  birthDate: Date;
  age: number;
  isValid: boolean;
}

/**
 * Parses a Romanian CNP and extracts gender, birth date, and age
 * @param cnp - The CNP string to parse
 * @returns CNPData object with extracted information
 */
export function parseCNP(cnp: string): CNPData {
  if (!cnp || cnp.length !== 13 || !/^\d+$/.test(cnp)) {
    return {
      gender: 'masculin',
      birthDate: new Date(),
      age: 0,
      isValid: false
    };
  }

  try {
    // Extract gender from first digit
    const genderDigit = parseInt(cnp[0]);
    const gender: 'masculin' | 'feminin' = genderDigit % 2 === 1 ? 'masculin' : 'feminin';

    // Extract birth date components
    const yearDigits = cnp.substring(1, 3);
    const monthDigits = cnp.substring(3, 5);
    const dayDigits = cnp.substring(5, 7);

    // Determine century based on first digit
    let century: number;
    switch (genderDigit) {
      case 1:
      case 2:
        century = 1900;
        break;
      case 3:
      case 4:
        century = 1800;
        break;
      case 5:
      case 6:
        century = 2000;
        break;
      default:
        century = 1900;
    }

    const year = century + parseInt(yearDigits);
    const month = parseInt(monthDigits) - 1; // JavaScript months are 0-indexed
    const day = parseInt(dayDigits);

    const birthDate = new Date(year, month, day);
    
    // Calculate age
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return {
      gender,
      birthDate,
      age,
      isValid: true
    };
  } catch (error) {
    console.error('Error parsing CNP:', error);
    return {
      gender: 'masculin',
      birthDate: new Date(),
      age: 0,
      isValid: false
    };
  }
}

/**
 * Generates an Epicriză template with patient data from CNP
 * @param cnpData - Parsed CNP data
 * @returns Formatted Epicriză template string
 */
export function generateEpicrizaTemplate(cnpData: CNPData): string {
  const today = new Date();
  const formattedDate = today.toLocaleDateString('ro-RO', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });

  return `Pacientul, în vârstă de ${cnpData.age} ani, sex ${cnpData.gender}, s-a prezentat pentru evaluare în cadrul comisiei medicale multidisciplinare, formată din specialiști în pneumologie, oncologie, chirurgie toracică, radiologie și anatomopatologie, în vederea investigării ...

- Istoricul medical:
Simptome: 
Antecedente: 

- Investigații efectuate:
Imagistică:
Radiografie toracică:
Bronhoscopie: 
Biopsie:
Markeri tumorali:`;
}

/**
 * Generates a Recomandări template with patient data from CNP
 * @param cnpData - Parsed CNP data
 * @returns Formatted Recomandări template string
 */
export function generateRecomandariTemplate(cnpData: CNPData): string {
  const today = new Date();
  const formattedDate = today.toLocaleDateString('ro-RO', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });

  return `- Discursul comisiei:
Cazul a fost discutat în cadrul comisiei medicale din data de ${formattedDate}`;
}
